import { FC, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Link,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import { conviteService } from '@/services/candidaturaService'
import { ConvitePrefill, ExperienciaRequest } from '@/types/candidatura'
import { maskCPF } from '@/utils/masks'
import { validateCPF } from '@/utils/validators'
import { mascararCnpj, normalizarCnpj } from '@/utils/cnpj'

/**
 * Cadastro completo do consultor por convite (PDF do Consultor, Etapa 2).
 *
 * <p>Público: quem preenche ainda não tem conta — é este envio que a cria. O e-mail é
 * apenas exibido, nunca editável: ele vem da candidatura aprovada, e deixar trocá-lo
 * permitiria usar o convite de alguém para criar a conta de outra pessoa.
 *
 * <p>Não há "Modalidade de Trabalho", "Raio de Atuação" nem "Especialidade": o PDF
 * questiona os três e pede a remoção na falta de aplicação prática. No lugar estão as
 * perguntas que ele próprio sugere.
 */

const UFS = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG',
  'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO',
]

const VERSAO_TERMOS = 'v1.0'

const experienciaVazia = (): ExperienciaRequest => ({ empresa: '', cargo: '', periodo: '' })

const CadastroConvitePage: FC = () => {
  const { token = '' } = useParams()
  const navigate = useNavigate()

  const [prefill, setPrefill] = useState<ConvitePrefill | null>(null)
  const [carregando, setCarregando] = useState(true)
  const [conviteInvalido, setConviteInvalido] = useState<string | null>(null)

  const [form, setForm] = useState({
    nomeCompleto: '',
    cpf: '',
    dataNascimento: '',
    celular: '',
    telefone: '',
    senha: '',
    confirmarSenha: '',
    cep: '',
    logradouro: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    uf: '',
    banco: '',
    agencia: '',
    conta: '',
    tipoConta: '',
    chavePix: '',
    cnpj: '',
    formacao: '',
    segmentosExperiencia: '',
    mercadoOrigem: '',
    tiposEmpresaAtendidos: '',
    produtosComercializados: '',
    principalRelacionamento: '',
    regiaoAtuacao: '',
    resumoProfissional: '',
  })
  const [experiencias, setExperiencias] = useState<ExperienciaRequest[]>([experienciaVazia()])
  const [aceite, setAceite] = useState(false)
  const [enviando, setEnviando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [concluido, setConcluido] = useState(false)

  useEffect(() => {
    let vivo = true
    conviteService
      .abrir(token)
      .then((p) => {
        if (!vivo) return
        setPrefill(p)
        setForm((f) => ({ ...f, nomeCompleto: p.nome }))
      })
      .catch((e) => {
        if (vivo) setConviteInvalido((e as { message?: string })?.message ?? 'Convite inválido ou expirado.')
      })
      .finally(() => {
        if (vivo) setCarregando(false)
      })
    return () => {
      vivo = false
    }
  }, [token])

  const set = (campo: keyof typeof form) => (valor: string) =>
    setForm((f) => ({ ...f, [campo]: valor }))

  const cpfValido = validateCPF(form.cpf)
  const senhasConferem = !!form.senha && form.senha === form.confirmarSenha
  const podeEnviar =
    form.nomeCompleto.trim().length >= 3 &&
    cpfValido &&
    !!form.dataNascimento &&
    form.celular.trim().length >= 8 &&
    form.senha.length >= 8 &&
    senhasConferem &&
    !!form.cidade.trim() &&
    !!form.uf &&
    aceite

  const enviar = async () => {
    if (!podeEnviar) return
    setEnviando(true)
    setErro(null)
    try {
      await conviteService.cadastrar(token, {
        nomeCompleto: form.nomeCompleto.trim(),
        cpf: form.cpf.replace(/\D/g, ''),
        dataNascimento: form.dataNascimento,
        celular: form.celular.trim(),
        telefone: form.telefone.trim() || undefined,
        senha: form.senha,
        cep: form.cep.trim() || undefined,
        logradouro: form.logradouro.trim() || undefined,
        numero: form.numero.trim() || undefined,
        complemento: form.complemento.trim() || undefined,
        bairro: form.bairro.trim() || undefined,
        cidade: form.cidade.trim(),
        uf: form.uf,
        banco: form.banco.trim() || undefined,
        agencia: form.agencia.trim() || undefined,
        conta: form.conta.trim() || undefined,
        tipoConta: form.tipoConta || undefined,
        chavePix: form.chavePix.trim() || undefined,
        cnpj: normalizarCnpj(form.cnpj) || undefined,
        formacao: form.formacao.trim() || undefined,
        segmentosExperiencia: form.segmentosExperiencia.trim() || undefined,
        mercadoOrigem: form.mercadoOrigem.trim() || undefined,
        tiposEmpresaAtendidos: form.tiposEmpresaAtendidos.trim() || undefined,
        produtosComercializados: form.produtosComercializados.trim() || undefined,
        principalRelacionamento: form.principalRelacionamento.trim() || undefined,
        regiaoAtuacao: form.regiaoAtuacao.trim() || undefined,
        resumoProfissional: form.resumoProfissional.trim() || undefined,
        // Linhas em branco não viram experiência: o formulário começa com uma vazia.
        experiencias: experiencias.filter((e) => e.empresa.trim() && e.cargo.trim()),
        aceiteTermos: aceite,
        versaoTermos: VERSAO_TERMOS,
      })
      setConcluido(true)
    } catch (e) {
      setErro((e as { message?: string })?.message ?? 'Não foi possível concluir o cadastro.')
    } finally {
      setEnviando(false)
    }
  }

  if (carregando) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    )
  }

  if (conviteInvalido) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
        <Card sx={{ maxWidth: 480, width: '100%' }}>
          <CardContent sx={{ textAlign: 'center', py: 5 }}>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              Convite indisponível
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {conviteInvalido}
            </Typography>
            <Button variant="outlined" onClick={() => navigate('/login')}>
              Ir para o início
            </Button>
          </CardContent>
        </Card>
      </Box>
    )
  }

  if (concluido) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
        <Card sx={{ maxWidth: 520, width: '100%' }}>
          <CardContent sx={{ textAlign: 'center', py: 5 }}>
            <CheckCircleOutlineIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" fontWeight={700} gutterBottom>
              Cadastro enviado
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              A Originei vai validar suas informações. Assim que o acesso for liberado, você
              poderá entrar com o e-mail <strong>{prefill?.email}</strong> e a senha que acabou
              de definir.
            </Typography>
            <Button variant="contained" onClick={() => navigate('/login')}>
              Ir para o login
            </Button>
          </CardContent>
        </Card>
      </Box>
    )
  }

  return (
    <Box sx={{ minHeight: '100vh', py: 5, px: 2, display: 'flex', justifyContent: 'center' }}>
      <Card sx={{ maxWidth: 860, width: '100%' }}>
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            Cadastro de Consultor
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Convite para <strong>{prefill?.email}</strong>
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 3 }}>
            {/* O e-mail não é editável de propósito: o convite é nominal. */}
            O e-mail é o do convite e não pode ser alterado.
          </Typography>

          {erro && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {erro}
            </Alert>
          )}

          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1.5 }}>Dados pessoais</Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField label="Nome completo" required fullWidth size="small"
                value={form.nomeCompleto} onChange={(e) => set('nomeCompleto')(e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField label="CPF" required fullWidth size="small"
                value={form.cpf}
                onChange={(e) => set('cpf')(maskCPF(e.target.value))}
                error={!!form.cpf && !cpfValido}
                helperText={!!form.cpf && !cpfValido ? 'CPF inválido' : ' '} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField label="Data de nascimento" type="date" required fullWidth size="small"
                InputLabelProps={{ shrink: true }}
                value={form.dataNascimento} onChange={(e) => set('dataNascimento')(e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Celular / WhatsApp" required fullWidth size="small"
                value={form.celular} onChange={(e) => set('celular')(e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Telefone" fullWidth size="small"
                value={form.telefone} onChange={(e) => set('telefone')(e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Senha" type="password" required fullWidth size="small"
                value={form.senha} onChange={(e) => set('senha')(e.target.value)}
                helperText="Mínimo de 8 caracteres" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Confirmar senha" type="password" required fullWidth size="small"
                value={form.confirmarSenha} onChange={(e) => set('confirmarSenha')(e.target.value)}
                error={!!form.confirmarSenha && !senhasConferem}
                helperText={!!form.confirmarSenha && !senhasConferem ? 'As senhas não conferem' : ' '} />
            </Grid>
          </Grid>

          <Typography variant="subtitle2" sx={{ mt: 3, mb: 1.5 }}>Endereço</Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={3}>
              <TextField label="CEP" fullWidth size="small"
                value={form.cep} onChange={(e) => set('cep')(e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Logradouro" fullWidth size="small"
                value={form.logradouro} onChange={(e) => set('logradouro')(e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField label="Número" fullWidth size="small"
                value={form.numero} onChange={(e) => set('numero')(e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label="Complemento" fullWidth size="small"
                value={form.complemento} onChange={(e) => set('complemento')(e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label="Bairro" fullWidth size="small"
                value={form.bairro} onChange={(e) => set('bairro')(e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField label="Cidade" required fullWidth size="small"
                value={form.cidade} onChange={(e) => set('cidade')(e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={1}>
              <TextField label="UF" select required fullWidth size="small"
                value={form.uf} onChange={(e) => set('uf')(e.target.value)}>
                {UFS.map((uf) => (
                  <MenuItem key={uf} value={uf}>{uf}</MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>

          <Typography variant="subtitle2" sx={{ mt: 3, mb: 0.5 }}>
            Dados bancários e fiscais
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
            {/* "Quando aplicável", diz o PDF — travar o cadastro aqui empurraria o
                candidato a inventar número para conseguir avançar. */}
            Opcionais agora; necessários para o pagamento de comissões.
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField label="Banco" fullWidth size="small"
                value={form.banco} onChange={(e) => set('banco')(e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField label="Agência" fullWidth size="small"
                value={form.agencia} onChange={(e) => set('agencia')(e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField label="Conta" fullWidth size="small"
                value={form.conta} onChange={(e) => set('conta')(e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField label="Tipo de conta" select fullWidth size="small"
                value={form.tipoConta} onChange={(e) => set('tipoConta')(e.target.value)}>
                <MenuItem value="CORRENTE">Corrente</MenuItem>
                <MenuItem value="POUPANCA">Poupança</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Chave PIX" fullWidth size="small"
                value={form.chavePix} onChange={(e) => set('chavePix')(e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="CNPJ (se emite nota como PJ)" fullWidth size="small"
                value={form.cnpj} onChange={(e) => set('cnpj')(mascararCnpj(e.target.value))}
                placeholder="00.000.000/0000-00" />
            </Grid>
          </Grid>

          <Typography variant="subtitle2" sx={{ mt: 3, mb: 1.5 }}>
            Experiência profissional
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField label="Formação acadêmica" fullWidth size="small"
                value={form.formacao} onChange={(e) => set('formacao')(e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Região principal de atuação" fullWidth size="small"
                value={form.regiaoAtuacao} onChange={(e) => set('regiaoAtuacao')(e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Em quais segmentos possui experiência?" fullWidth size="small"
                value={form.segmentosExperiencia}
                onChange={(e) => set('segmentosExperiencia')(e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="De qual mercado você vem?" fullWidth size="small"
                value={form.mercadoOrigem} onChange={(e) => set('mercadoOrigem')(e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Quais tipos de empresa costuma atender?" fullWidth size="small"
                value={form.tiposEmpresaAtendidos}
                onChange={(e) => set('tiposEmpresaAtendidos')(e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Quais produtos financeiros já comercializou?" fullWidth size="small"
                value={form.produtosComercializados}
                onChange={(e) => set('produtosComercializados')(e.target.value)} />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Qual é o seu principal relacionamento comercial?" fullWidth size="small"
                value={form.principalRelacionamento}
                onChange={(e) => set('principalRelacionamento')(e.target.value)} />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Resumo da sua experiência" fullWidth size="small"
                multiline minRows={3}
                value={form.resumoProfissional}
                onChange={(e) => set('resumoProfissional')(e.target.value)} />
            </Grid>
          </Grid>

          <Typography variant="subtitle2" sx={{ mt: 3, mb: 0.5 }}>
            Empresas em que trabalhou
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
            Linhas incompletas são ignoradas no envio.
          </Typography>
          <Stack spacing={2} sx={{ mb: 2 }}>
            {experiencias.map((exp, i) => (
              <Paper key={i} variant="outlined" sx={{ p: 2 }}>
                <Grid container spacing={2} alignItems="flex-start">
                  <Grid item xs={12} sm={4}>
                    <TextField label="Empresa" fullWidth size="small" value={exp.empresa}
                      inputProps={{ 'aria-label': `Empresa ${i + 1}` }}
                      onChange={(e) =>
                        setExperiencias((lista) =>
                          lista.map((x, idx) => (idx === i ? { ...x, empresa: e.target.value } : x))
                        )
                      } />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField label="Cargo" fullWidth size="small" value={exp.cargo}
                      inputProps={{ 'aria-label': `Cargo ${i + 1}` }}
                      onChange={(e) =>
                        setExperiencias((lista) =>
                          lista.map((x, idx) => (idx === i ? { ...x, cargo: e.target.value } : x))
                        )
                      } />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <TextField label="Período" fullWidth size="small" value={exp.periodo ?? ''}
                      placeholder="2018–2023"
                      onChange={(e) =>
                        setExperiencias((lista) =>
                          lista.map((x, idx) => (idx === i ? { ...x, periodo: e.target.value } : x))
                        )
                      } />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <TextField label="Segmentos" fullWidth size="small" value={exp.segmentos ?? ''}
                      onChange={(e) =>
                        setExperiencias((lista) =>
                          lista.map((x, idx) => (idx === i ? { ...x, segmentos: e.target.value } : x))
                        )
                      } />
                  </Grid>
                  <Grid item xs={12} sm={1}>
                    <IconButton
                      aria-label={`Remover experiência ${i + 1}`}
                      disabled={experiencias.length === 1}
                      onClick={() => setExperiencias((lista) => lista.filter((_, idx) => idx !== i))}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField label="Principais atividades" fullWidth size="small" multiline minRows={2}
                      value={exp.atividades ?? ''}
                      onChange={(e) =>
                        setExperiencias((lista) =>
                          lista.map((x, idx) => (idx === i ? { ...x, atividades: e.target.value } : x))
                        )
                      } />
                  </Grid>
                </Grid>
              </Paper>
            ))}
          </Stack>
          <Button
            startIcon={<AddIcon />}
            onClick={() => setExperiencias((lista) => [...lista, experienciaVazia()])}
            disabled={experiencias.length >= 30}
          >
            Adicionar empresa
          </Button>

          <Divider sx={{ my: 3 }} />

          <FormControlLabel
            control={<Checkbox checked={aceite} onChange={(e) => setAceite(e.target.checked)} />}
            label={
              <Typography variant="body2">
                Li e aceito os{' '}
                <Link href="/termos" target="_blank" rel="noopener noreferrer">termos e contratos</Link>
                {' '}e a{' '}
                <Link href="/privacidade" target="_blank" rel="noopener noreferrer">política de privacidade</Link>
                {' '}da Originei.
              </Typography>
            }
          />

          <Button
            variant="contained"
            size="large"
            fullWidth
            sx={{ mt: 3 }}
            onClick={enviar}
            disabled={!podeEnviar || enviando}
            startIcon={enviando ? <CircularProgress size={18} color="inherit" /> : undefined}
          >
            {enviando ? 'Enviando…' : 'Concluir cadastro'}
          </Button>
        </CardContent>
      </Card>
    </Box>
  )
}

export default CadastroConvitePage
